import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  FaPlus, FaTrashAlt, FaUpload, FaCheckCircle, FaExclamationCircle, 
  FaUser, FaBuilding, FaBriefcase, FaCalendarAlt, FaClock, FaFileAlt, FaTimes,
  FaSave, FaSpinner
} from 'react-icons/fa';
import UploadDocumentModal from './UploadDocumentModal';
import { fetchProjects, getActivitiesByProjectId } from '../api/projectAndActivityApis';

export default function TimesheetFormModal({ isOpen, onClose, onSubmitSuccess }) {
  const [formData, setFormData] = useState(() => {
    const storedUser = localStorage.getItem('user');
    const user = storedUser ? JSON.parse(storedUser) : null;
    return {
      name: (user?.firstName && user?.lastName) ? `${user.firstName} ${user.lastName}` : '',
      department: user?.department || '',
      role: user?.role || '', 
      frequency: 'daily',
    };
  });

  const [entries, setEntries] = useState([{ date: '', hours: '', projectId: '', activityId: '', tasksLogged: '' }]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [entryActivities, setEntryActivities] = useState({});
  // const [loading, setLoading] = useState(false); // Removed as it was unused
  const [submitting, setSubmitting] = useState(false);
  
  const modalRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user'));
    const staffId = user?.id;

    if (!staffId || !token) return;

    // Fetch user profile
  // Replace lines 44-58 with this:
axios.get(`https://ims.holdthechild.net/api/staff/${staffId}/me`, {
  headers: { Authorization: `Bearer ${token}` }
})
  .then(res => {
    // Add defensive checking for the response structure
    const userData = res.data?.data || res.data;
    
    if (userData && userData.firstName) {
      const { firstName, lastName, department, role } = userData;
      setFormData(prev => ({
        ...prev,
        name: `${firstName} ${lastName}`,
        department: department || '',
        role: role || prev.role,
      }));
    } else {
      console.warn("User profile data structure unexpected:", res.data);
      toast.warn("Could not load complete user profile details.");
    }
  })
  .catch(err => {
    console.error("Error fetching user profile:", err);
    console.error("Response data:", err.response?.data);
    toast.warn("Could not confirm user profile details."); 
  });

    // Fetch projects
    fetchProjects().then(data => {
      setProjects(data);
    }).catch(err => {
      console.error("Failed to load projects:", err);
      toast.error("Failed to load projects/activities.");
    });
  }, []);

  // Effect to fetch activities for each entry when its project ID changes
  useEffect(() => {
    entries.forEach((entry) => {
      if (entry.projectId && !entryActivities[entry.projectId]) {
        getActivitiesByProjectId(entry.projectId).then(data => {
          setEntryActivities(prev => ({ ...prev, [entry.projectId]: data }));
        }).catch(err => {
          console.error("Failed to load activities:", err);
          setEntryActivities(prev => ({ ...prev, [entry.projectId]: [] }));
        });
      }
    });
  }, [entries, entryActivities]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if upload modal is open or if clicking inside a form element (e.g., date picker popup)
      if (isUploadModalOpen || (event.target.closest('select, input, textarea'))) {
        return;
      }

      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscapeKey = (event) => {
      // Don't close if upload modal is open
      if (event.key === 'Escape' && !isUploadModalOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose, isUploadModalOpen]);

  const handleFormChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  
  const handleEntryChange = (index, e) => {
    const { name, value } = e.target;
    const newEntries = [...entries];
    newEntries[index][name] = value;
    setEntries(newEntries);
  };

  const handleProjectChange = (index, projectId) => {
    const newEntries = [...entries];
    newEntries[index].projectId = projectId;
    newEntries[index].activityId = ''; // Reset activity when project changes
    setEntries(newEntries);
  };

  const handleActivityChange = (index, activityId) => {
    const newEntries = [...entries];
    newEntries[index].activityId = activityId;
    setEntries(newEntries);
  };

  const addEntry = () => setEntries([...entries, { date: '', hours: '', projectId: '', activityId: '', tasksLogged: '' }]);
  const removeEntry = (index) => setEntries(prev => prev.filter((_, i) => i !== index));

  const validateForm = () => {
    // Note: Keeping all original checks as they seem required by the application logic
    if (!formData.name.trim()) {
      toast.error("Please ensure your full name is loaded.");
      return false;
    }
    if (!formData.department.trim()) {
      toast.error("Department is missing.");
      return false;
    }
    if (!formData.role.trim()) {
      toast.error("Role is missing.");
      return false;
    }
    
    // Validate each entry
    for (const entry of entries) {
      const isDaily = formData.frequency === 'daily';
      const dateField = isDaily ? entry.date : entry.weekEnding;
      const dateFieldName = isDaily ? 'date' : 'week ending date';
      
      if (!dateField) {
        toast.error(`Please select a ${dateFieldName} for all entries.`);
        return false;
      }
      
      const hours = Number(entry.hours);
      if (!entry.hours || isNaN(hours) || hours <= 0) {
        toast.error("Please enter valid hours (greater than 0) for all entries.");
        return false;
      }
    }
    return true;
  };

  const handleUploadSuccess = (files) => {
    // This assumes files is an array of objects: [{fileUrl, originalName}, ...]
    setUploadedFiles(files);
    setIsUploadModalOpen(false);
    toast.success(`${files.length} document(s) uploaded successfully!`);
  };

  const resetForm = () => {
    const storedUser = localStorage.getItem('user');
    const user = storedUser ? JSON.parse(storedUser) : null;
    setFormData({
      name: (user?.firstName && user?.lastName) ? `${user.firstName} ${user.lastName}` : '',
      department: user?.department || '',
      role: user?.role || '',
      frequency: 'daily'
    });
    setEntries([{ date: '', hours: '', projectId: '', activityId: '', tasksLogged: '' }]);
    setUploadedFiles([]);
    setStatus({ type: '', message: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (submitting) return; // Prevent double submission
    
    if (!validateForm()) return;
    
    setSubmitting(true);
    toast.info("Submitting timesheet...", { toastId: 'submission-pending' });

    try {
      const token = localStorage.getItem("token");
      const user = JSON.parse(localStorage.getItem('user'));
      // const staffId = user?.id; // Not directly needed in payload but good for check

      if (!token || !user?.id) {
        toast.update('submission-pending', { render: "Authentication failed. Please log in again.", type: "error", isLoading: false, autoClose: 5000 });
        setSubmitting(false);
        return;
      }

      const entriesToSend = entries.map(entry => {
        const selectedProject = projects.find(p => p._id === entry.projectId);
        const selectedActivity = (entryActivities[entry.projectId] || []).find(a => a._id === entry.activityId);

        // Date logic: use weekEnding if frequency is weekly, otherwise use date
        const entryDate = formData.frequency === 'weekly' ? entry.weekEnding : entry.date;
        
        return {
          date: entryDate,
          project: selectedProject ? selectedProject.projectTitle : '',
          activity: selectedActivity ? selectedActivity.activityTitle : '',
          hours: Number(entry.hours),
          tasksLogged: entry.tasksLogged || ''
        };
      });

      const payload = {
        ...formData,
        entries: entriesToSend,
        supportingDocs: uploadedFiles.map(file => ({
          fileUrl: file.fileUrl,
          originalName: file.originalName
        }))
      };

      await axios.post(
        "https://ims.holdthechild.net/api/timesheets",
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update toast for success
      toast.update('submission-pending', { 
        render: "🎉 Timesheet submitted successfully!", 
        type: "success", 
        isLoading: false, 
        autoClose: 5000 
      });
      setStatus({ type: 'success', message: 'Timesheet submitted successfully!' });
      
      resetForm();
      if (onSubmitSuccess) onSubmitSuccess();
      onClose();

    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Submission failed. Please try again.';
      console.error("Timesheet submission failed:", err.response?.data || err.message);
      
      // Update toast for failure
      toast.update('submission-pending', { 
        render: `❌ ${errorMessage}`, 
        type: "error", 
        isLoading: false, 
        autoClose: 5000 
      });
      setStatus({ type: 'error', message: errorMessage });

    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      {/* Absolute Loading Overlay */}
      {submitting && (
        <div className="absolute inset-0 z-[60] flex items-center justify-center bg-black/70">
          <div className="bg-white rounded-xl p-8 max-w-sm mx-4 text-center shadow-2xl">
            <div className="flex justify-center mb-4">
              <FaSpinner className="w-12 h-12 text-orange-500 animate-spin" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Submitting Timesheet</h3>
            <p className="text-gray-600">Please wait while we process your timesheet...</p>
          </div>
        </div>
      )}
      
      <div
        ref={modalRef}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col overflow-hidden transform transition-all duration-300 ease-out"
      >
        {/* Header with Orange Gradient */}
        <div className="flex-shrink-0 bg-gradient-to-r from-orange-500 to-orange-600 border-b border-orange-50">
          <div className="flex items-center justify-between px-6 py-5">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl shadow-sm">
                <FaClock className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Submit Timesheet</h2>
                <p className="text-orange-100 mt-1">Time Tracking System</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-xl transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white hover:shadow-sm"
            >
              <FaTimes className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Wrap body and footer in the form to enable submit button functionality */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-grow overflow-hidden">
          
          {/* Body (Scrollable Content) */}
          <div className="flex-grow px-6 py-5 overflow-y-auto space-y-6">
            
            {/* Status Message Block */}
            {status.message && (
              <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${status.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                {status.type === 'success' ? <FaCheckCircle className="text-green-500 flex-shrink-0" /> : <FaExclamationCircle className="text-red-500 flex-shrink-0" />}
                <span>{status.message}</span>
              </div>
            )}

            {/* User Info Section */}
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <FaUser className="text-orange-500" /> Employee Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaUser className="h-4 w-4 text-gray-400" />
                    </div>
                    <input 
                      type="text" 
                      name="name" 
                      value={formData.name} 
                      readOnly 
                      className="w-full pl-10 pr-3 py-2.5 text-sm rounded-lg border border-gray-300 bg-gray-100 text-gray-700" 
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaBuilding className="h-4 w-4 text-gray-400" />
                    </div>
                    <input 
                      type="text" 
                      name="department" 
                      value={formData.department} 
                      readOnly 
                      className="w-full pl-10 pr-3 py-2.5 text-sm rounded-lg border border-gray-300 bg-gray-100 text-gray-700" 
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaBriefcase className="h-4 w-4 text-gray-400" />
                    </div>
                    <input 
                      type="text" 
                      name="role" 
                      value={formData.role} 
                      readOnly 
                      className="w-full pl-10 pr-3 py-2.5 text-sm rounded-lg border border-gray-300 bg-gray-100 text-gray-700" 
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaCalendarAlt className="h-4 w-4 text-gray-400" />
                    </div>
                    <select 
                      name="frequency" 
                      value={formData.frequency} 
                      onChange={handleFormChange}
                      className="w-full pl-10 pr-3 py-2.5 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Timesheet Entries Section */}
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-200 shadow-sm">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <FaClock className="text-orange-500" /> Timesheet Entries
                </h3>
                <button
                  type="button"
                  onClick={addEntry}
                  className="flex items-center gap-1 px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors shadow-sm"
                >
                  <FaPlus /> Add Entry
                </button>
              </div>
              
              <div className="space-y-4">
                {entries.map((entry, index) => {
                  const activitiesForEntry = entryActivities[entry.projectId] || [];
                  return (
                    <div key={index} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {formData.frequency === 'daily' ? 'Date' : 'Week Ending'}
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <FaCalendarAlt className="h-4 w-4 text-gray-400" />
                            </div>
                            <input
                              type="date"
                              name={formData.frequency === 'daily' ? 'date' : 'weekEnding'}
                              value={formData.frequency === 'daily' ? entry.date : entry.weekEnding || ''}
                              onChange={(e) => handleEntryChange(index, e)}
                              className="w-full pl-10 pr-3 py-2.5 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                              required 
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Hours</label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <FaClock className="h-4 w-4 text-gray-400" />
                            </div>
                            <input
                              type="number"
                              name="hours"
                              step="0.1" // Allow decimal hours
                              min="0.1"
                              value={entry.hours}
                              onChange={(e) => handleEntryChange(index, e)}
                              className="w-full pl-10 pr-3 py-2.5 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                              required 
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Project (Optional)</label>
                          <select
                            name="projectId"
                            value={entry.projectId}
                            onChange={(e) => handleProjectChange(index, e.target.value)}
                            className="w-full pl-3 pr-10 py-2.5 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none bg-white"
                          >
                            <option value="">-- Select Project --</option>
                            {projects.map(project => (
                              <option key={project._id} value={project._id}>
                                {project.projectTitle || 'Unnamed Project'}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Activity (Optional)</label>
                          <select
                            name="activityId"
                            value={entry.activityId}
                            onChange={(e) => handleActivityChange(index, e.target.value)}
                            className="w-full pl-3 pr-10 py-2.5 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none bg-white disabled:bg-gray-100"
                            disabled={!entry.projectId || activitiesForEntry.length === 0}
                          >
                            <option value="">-- Select Activity --</option>
                            {activitiesForEntry.map(activity => (
                              <option key={activity._id} value={activity._id}>
                                {activity.activityTitle || 'Unnamed Activity'}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Task Description</label>
                          <textarea
                            name="tasksLogged"
                            value={entry.tasksLogged}
                            onChange={(e) => handleEntryChange(index, e)}
                            rows="1"
                            placeholder="Brief task summary"
                            className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          />
                        </div>
                      </div>
                      
                      {entries.length > 1 && (
                        <div className="flex justify-end mt-3">
                          <button
                            type="button"
                            onClick={() => removeEntry(index)}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <FaTrashAlt /> Remove Entry
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Supporting Documents Section */}
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <FaFileAlt className="text-orange-500" /> Supporting Documents
              </h3>
              
              <button
                type="button"
                onClick={() => setIsUploadModalOpen(true)}
                className="w-full border-2 border-dashed border-gray-300 rounded-xl p-6 text-center text-gray-500 hover:border-orange-500 hover:text-orange-500 transition-colors"
              >
                <FaUpload className="mx-auto h-8 w-8 mb-2" />
                <span className="block">Click to Upload Documents</span>
              </button>
              
              {uploadedFiles.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Uploaded Files:</h4>
                  <ul className="space-y-2">
                    {uploadedFiles.map((file, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm bg-white p-2 rounded-lg border border-gray-200">
                        <FaCheckCircle className="text-green-500 flex-shrink-0" />
                        <a href={file.fileUrl} target="_blank" rel="noopener noreferrer" className="truncate hover:underline text-blue-600">
                          {file.originalName}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Footer (Fixed Actions) */}
          <div className="flex-shrink-0 bg-gray-50 px-6 py-5 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <FaExclamationCircle className="w-4 h-4 text-orange-500" />
                <span>Ensure all required fields are filled out before submitting.</span>
              </div>

              <div className="flex items-center space-x-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 text-gray-700 hover:bg-gray-200 border border-gray-300"
                >
                  Cancel
                </button>

                <button
                  type="submit" // Correctly triggers the form's onSubmit
                  disabled={submitting}
                  className={`relative px-8 py-3 rounded-xl font-semibold text-white transition-all duration-200 transform ${
                    submitting
                      ? 'bg-orange-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 hover:scale-105 shadow-lg hover:shadow-xl'
                  }`}
                >
                  {submitting ? (
                    <div className="flex items-center space-x-3">
                      <FaSpinner className="w-5 h-5 animate-spin" />
                      <span>Submitting...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <FaSave />
                      <span>Submit Timesheet</span>
                    </div>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
      
      {isUploadModalOpen && (
        <UploadDocumentModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          staffId={JSON.parse(localStorage.getItem('user')).id}
          onUploadSuccessWithFiles={handleUploadSuccess}
          category="timesheet"
        />
      )}
    </div>
  );
}